import { Box } from '@mui/material';
import { useMemo, useState } from 'react';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { FilterLinesIcon } from '@/components/icons/FilterLinesIcon';
import { StyledCounter } from '@/components/notifications/StyledCounter/StyledCounter';

import { FilterGroupNav } from './FilterGroupNav';
import { TableFilterProps } from './types';
import { clearAllSelections, countTotalSelected, toggleOption } from './utils';

/**
 * A generic table filter component that displays a button with a popover
 * containing filter options organized into groups.
 */
export function TableFilter({
  groups,
  value,
  onChange,
  buttonProps = {},
  popperProps = {},
}: TableFilterProps) {
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);

  const activeItemsCount = useMemo(() => {
    return countTotalSelected(value);
  }, [value]);

  // Handlers for the filter actions
  const handleToggleOption = (groupId: string, optionValue: string) => {
    // Find the group to get its multi property
    const group = groups.find((g) => g.id === groupId);
    if (!group) return;

    // Create updated value with toggled option
    const newValue = toggleOption(
      groupId,
      optionValue,
      group.multi !== false, // Default to true
      value
    );

    onChange(newValue);
  };

  const handleClearAll = () => {
    onChange(clearAllSelections(value));
  };

  const handleSelectGroup = (groupId: string | null) => {
    setActiveGroupId(groupId);
  };

  // Default button props
  const { label = 'Filter', size = 'sm', variant = 'secondary' } = buttonProps;

  return (
    <Box>
      <ButtonWithPopover
        label={label}
        size={size}
        variant={variant}
        startIcon={() => (
          <StyledCounter
            show={!!activeItemsCount}
            badgeContent={activeItemsCount}
            color="error"
            overlap="circular"
          >
            <FilterLinesIcon size={16} />
          </StyledCounter>
        )}
        popperVariant="menuBelow"
        onClose={() => handleSelectGroup(null)}
        popperProps={{
          ...popperProps,
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
        }}
      >
        <FilterGroupNav
          groups={groups}
          value={value}
          activeGroupId={activeGroupId}
          onSelectGroup={handleSelectGroup}
          onClearAll={handleClearAll}
          onToggleOption={handleToggleOption}
        />
      </ButtonWithPopover>
    </Box>
  );
}
